<template>
  <div>
    <md-card id="my-settings" align="Left" style="margin-top: 4rem;">
      <md-card-header class="md-card-header-icon md-card-header-green">
        <div class="card-icon">
          <i class="material-icons">account_circle</i>
        </div>
        <h4 class="title">My Profile</h4>

        <div class="md-layout md-gutter md-layout-item md-size-100 md-alignment-center-left" style="padding: 0;">
          <div class="section-spacing">
            <personal-info/>
          </div>
          <div class="section-spacing">
            <h3 class="title section-title">Credentials</h3>
            <credentials-info/>
          </div>
          <div class="section-spacing">
            <h3 class="title section-title">Practice</h3>
            <practice-info/>
          </div>
        </div>
      </md-card-header>
      <div style="display: flex; justify-content: flex-end; padding: 0 2rem 1rem 0">
        <md-button style="width: auto;" @click="saveEdit">Save</md-button>
      </div>
    </md-card>
    <loader :is-visible="isLoading" />
  </div>
</template>

<script>
import loader from "@/components/GlobeChek/loader.vue"
import { mapActions, mapGetters } from "vuex"
import PersonalInfo from "../Layout/ProfileForm/PersonalInfo.vue"
import CredentialsInfo from "../Layout/ProfileForm/CredentialsInfo.vue"
import PracticeInfo from "../Layout/ProfileForm/PracticeInfo.vue"
import Swal from "sweetalert2"

export default {
  name: "NewComponent",
  components: {
    loader,
    PersonalInfo,
    CredentialsInfo,
    PracticeInfo,
  },
  props: [] ,
  data() {
    return {
      isLoading: false,
      rawDoctorData: {},
      nextNavigation: null,
    }
  },
  computed: {
    ...mapGetters('doctorProfile', ['fullProfile', 'editInfo']),
  },
  created: function() {},
  async mounted() {
    await this.getDoctorDetail();
  },
  beforeRouteLeave(to, from, next) {
    if (!this.isLoading && this.hasChanges()) {
      this.confirmNavigation().then((result) => {
        next(!!result.value);
      });
      return;
    }
    next();
  },
  beforeRouteEnter(to, from, next) {
    const role = sessionStorage.getItem("role");
    const isDoctor = sessionStorage.getItem('isDoctor') === 'true';
    if (role === 'Reader' && !isDoctor) {
      next('/portal/reader/dashboard')
      return;
    }
    next();
  },
  updated: function() {},
  destroyed: function() {},
  methods: {
    ...mapActions('doctorProfile', ['fetchDoctorProfile', 'updatePersonalInfo']),
    hasChanges() {
      return JSON.stringify(this.rawDoctorData) !== JSON.stringify(this.fullProfile);
    },
    getDoctorDetail: async function () {
      try {
        this.isLoading = true;
        await this.fetchDoctorProfile();
        this.rawDoctorData = { ...this.fullProfile };
      } catch(error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    saveEdit: async function () {
      if (!this.hasChanges()) {
        await Swal.fire({
          title: 'No changes',
          text: 'There are no changes to save'
        })
        return;
      }
      const result = await Swal.fire({
        title: 'Save changes?',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, save!',
        cancelButtonText: 'Cancel'
      })
      if (result.value) {
        await this.handleConfirmSave();
      }
    },
    async handleConfirmSave() {
      try {
        this.isLoading = true;
        const payload = this.defineDoctorEditPayload();
        await this.updatePersonalInfo(payload);
        await this.getDoctorDetail();
        await Swal.fire({
          title: 'Saved!',
          text: 'Your changes have been saved',
          timer: 2000,
          showConfirmButton: false
        })
      } catch (error) {
        console.error('Error saving:', error);
        await Swal.fire({
          title: 'Save failed',
          text: 'An error occurred while saving'
        })
      } finally {
        this.isLoading = false;
      }
    },
    async confirmNavigation() {
      return await Swal.fire({
        title: 'Unsaved changes',
        text: 'You have unsaved changes. Are you sure you want to leave?',
        showCancelButton: true,
        confirmButtonText: 'Yes, leave',
        cancelButtonText: 'Stay'
      })
    },
    formatCertificationDate(dateString) {
      if (this.fullProfile.credentials.ruCertified !== 'Yes') return null;
      const dateObj = new Date(dateString)
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, "0") // Months are zero-based
      const day = String(dateObj.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    },
    defineDoctorEditPayload() {
      const fullProfileJSON = JSON.parse(JSON.stringify(this.fullProfile));
      const isMultipleOrgs = fullProfileJSON.editInfo.organisations.includes(";")
      if (isMultipleOrgs) {
        fullProfileJSON.editInfo.organisations = fullProfileJSON.editInfo.organisations.split(";")
      }
      if (!isMultipleOrgs) {
        fullProfileJSON.editInfo.organisations = [fullProfileJSON.editInfo.organisations]
      }
      return {
        id: fullProfileJSON.editInfo.id,
        personID: fullProfileJSON.editInfo.personID,
        speciality: fullProfileJSON.credentials.specialties,
        specialization: fullProfileJSON.credentials.specialization,
        subspecialization: fullProfileJSON.credentials.subSpecialization,
        qualification: fullProfileJSON.credentials.qualification,
        boardCertificate: fullProfileJSON.credentials.ruCertified,
        CAQH: fullProfileJSON.credentials.CAQH,
        NPI: fullProfileJSON.credentials.NPI,
        license: fullProfileJSON.credentials.LICENSE,
        insurance: fullProfileJSON.practice.insurances || "",
        status: 2,
        practice: fullProfileJSON.practice.bizName,
        webSite: fullProfileJSON.practice.website,
        certificationDate: this.formatCertificationDate(fullProfileJSON.credentials.certDate),
        businessPhoneNo: fullProfileJSON.practice.phone,
        isReader: fullProfileJSON.personal.isReader || false,
        userType: "Doctor",
        isApproved: false,
        isAccountCreated: false,
        organisations: fullProfileJSON.editInfo.organisations,
        regions: fullProfileJSON.editInfo.regions ? fullProfileJSON.editInfo.regions : [796],
        mailingAddress: {
          id: fullProfileJSON.practice.workAddressId || "",
          address1: fullProfileJSON.practice.address || "",
          address2: "",
          city: fullProfileJSON.personal.city || "",
          state: fullProfileJSON.personal.state.toString() || "",
          country: fullProfileJSON.personal.country.toString() || "",
          zipCode: fullProfileJSON.personal.zipCode || "",
          addressType: 1
        },
        businessAddress: {
          id: fullProfileJSON.personal.mainAddressID || "",
          address1: fullProfileJSON.practice.address || "",
          address2: "",
          city: fullProfileJSON.practice.city || "",
          state: fullProfileJSON.practice.state.toString() || "",
          country: fullProfileJSON.practice.country.toString() || "",
          zipCode: fullProfileJSON.practice.zip || "",
          addressType: 2
        },
        person: {
          id: fullProfileJSON.editInfo.personID || "",
          firstName: fullProfileJSON.personal.firstName || "",
          lastName: fullProfileJSON.personal.lastName || "",
          title: fullProfileJSON.personal.title || "",
          email: fullProfileJSON.personal.emailAddress || "",
          cellNumber: fullProfileJSON.personal.cellNumber || "",
          allowSMS: fullProfileJSON.personal.allowSMS,
          gender: fullProfileJSON.personal.gender || "",
          DOB: null,
          comments: fullProfileJSON.comments.comments || "",
          languages: fullProfileJSON.practice.languages.toString() || "",
          status: 2
        }
      };
    }
  }
}
</script>

<style lang="scss" scoped>
.md-app {
  min-height: 50dvh;
  width: 100%;
  padding: 1rem 1rem 1rem 0;
}

.md-drawer {
  width: 230px;
  max-width: calc(100vw - 125px);
}

.section-spacing {
  margin-bottom: 32px;
}

.section-title {
  margin-bottom: 15px;
  padding-bottom: 5px;
  padding-left: 1rem;
  border-bottom: 1px solid #eee;
}
</style>